import { useState, useEffect } from "react";
import { classroomService } from "@/services/classroomService";
import type { ClassroomSummary, RosterStudent } from "@/services/classroomService";
import { BookOpen, Users, AlertTriangle, ChevronRight, ArrowLeft, Shield, XCircle } from "lucide-react";
import { useTeacherTheme } from "@/contexts/TeacherThemeContext";

function riskLevel(c: ClassroomSummary): 'high' | 'medium' | 'low' {
  if (c.criticalAlerts > 0) return 'high';
  if (c.activeAlerts > 2) return 'medium';
  return 'low';
}

const RISK_STYLES = {
  high:   { bg: 'bg-red-50',     border: 'border-red-200',     badge: 'bg-red-100 text-red-700',    dot: 'bg-red-500' },
  medium: { bg: 'bg-amber-50',   border: 'border-amber-200',   badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  low:    { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
};

function ClassroomCard({ classroom, onClick, dm }: { classroom: ClassroomSummary; onClick: () => void; dm: boolean }) {
  const risk = riskLevel(classroom);
  const s = RISK_STYLES[risk];

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-5 rounded-xl border hover:shadow-md transition-all group ${dm ? `border-slate-700 bg-slate-800 hover:border-slate-600` : `${s.bg} ${s.border}`}`}
      style={{ cursor: 'pointer' }}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
            <BookOpen className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className={`text-sm font-bold group-hover:text-blue-500 transition ${dm ? 'text-slate-100' : 'text-slate-800'}`}>{classroom.name}</h3>
            <p className={`text-xs ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
              {[classroom.subject, classroom.gradeLevel, classroom.period && `Period ${classroom.period}`].filter(Boolean).join(' · ')}
            </p>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-blue-600 transition mt-1 shrink-0" />
      </div>

      <div className="mt-4 flex items-center gap-4">
        <div className={`flex items-center gap-1.5 text-xs ${dm ? 'text-slate-400' : 'text-slate-600'}`}>
          <Users className="h-3.5 w-3.5" />
          <span>{classroom.studentCount} student{classroom.studentCount !== 1 ? 's' : ''}</span>
        </div>
        {classroom.activeAlerts > 0 && (
          <div className={`flex items-center gap-1.5 text-xs ${dm ? 'text-slate-400' : 'text-slate-600'}`}>
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            <span>{classroom.activeAlerts} alert{classroom.activeAlerts !== 1 ? 's' : ''}</span>
          </div>
        )}
        {classroom.criticalAlerts > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
            {classroom.criticalAlerts} critical
          </span>
        )}
        <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-medium ${s.badge}`}>
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${s.dot} mr-1.5`} />
          {risk.charAt(0).toUpperCase() + risk.slice(1)} Risk
        </span>
      </div>
    </button>
  );
}

function RosterView({ classroom, onBack, dm }: { classroom: ClassroomSummary; onBack: () => void; dm: boolean }) {
  const [roster, setRoster] = useState<RosterStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    classroomService.getClassroomRoster(classroom.id)
      .then(setRoster)
      .catch(() => setError('Could not load roster.'))
      .finally(() => setLoading(false));
  }, [classroom.id]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 transition"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to classes
        </button>
      </div>

      <div className="flex items-center gap-4">
        <div className="h-12 w-12 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
          <BookOpen className="h-6 w-6 text-white" />
        </div>
        <div>
          <h2 className={`text-lg font-bold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>{classroom.name}</h2>
          <p className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
            {[classroom.subject, classroom.gradeLevel, classroom.period && `Period ${classroom.period}`].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="ml-auto flex gap-3 text-sm">
          <div className={`text-center px-3 py-1.5 rounded-lg border ${dm ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-100'}`}>
            <p className={`font-bold ${dm ? 'text-slate-200' : 'text-slate-800'}`}>{classroom.studentCount}</p>
            <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>Students</p>
          </div>
          <div className="text-center px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-100">
            <p className="font-bold text-amber-700">{classroom.activeAlerts}</p>
            <p className="text-xs text-amber-500">Alerts</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 text-slate-400 text-sm">Loading roster…</div>
      ) : error ? (
        <div className="flex items-center justify-center h-40 text-red-400 text-sm gap-2">
          <XCircle className="h-4 w-4" /> {error}
        </div>
      ) : roster.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-slate-400 text-sm gap-2">
          <Shield className="h-8 w-8 text-slate-200" />
          No students enrolled in this class
        </div>
      ) : (
        <div className="space-y-2">
          {roster.map(r => (
            <div key={r.student.id} className={`flex items-center gap-4 p-4 rounded-xl border transition ${dm ? 'bg-slate-800 border-slate-700 hover:border-slate-600' : 'bg-white border-slate-100 hover:border-slate-200'}`}>
              <div className="h-9 w-9 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                {(r.student.user.name || 'S').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
              </div>
              <div className="min-w-0">
                <p className={`text-sm font-semibold truncate ${dm ? 'text-slate-100' : 'text-slate-800'}`}>{r.student.user.name}</p>
                <p className={`text-xs truncate ${dm ? 'text-slate-500' : 'text-slate-400'}`}>{r.student.user.email}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function MyClasses() {
  const { dm } = useTeacherTheme();
  const [classrooms, setClassrooms] = useState<ClassroomSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ClassroomSummary | null>(null);

  useEffect(() => {
    classroomService.getSchoolClassrooms()
      .then(setClassrooms)
      .catch(() => setError('Could not load classrooms.'))
      .finally(() => setLoading(false));
  }, []);

  if (selected) {
    return <RosterView classroom={selected} onBack={() => setSelected(null)} dm={dm} />;
  }

  const high   = classrooms.filter(c => riskLevel(c) === 'high');
  const medium = classrooms.filter(c => riskLevel(c) === 'medium');
  const low    = classrooms.filter(c => riskLevel(c) === 'low');

  return (
    <div className="space-y-5">
      <div>
        <h2 className={`text-lg font-bold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>My Classes</h2>
        <p className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Click a class to view the student roster</p>
      </div>

      {/* Summary */}
      {!loading && !error && (
        <div className="flex gap-3 flex-wrap">
          {[
            { label: 'Total Classes', value: classrooms.length, style: dm ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-100 text-slate-800' },
            { label: 'High Risk',     value: high.length,       style: dm ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-red-50 border-red-100 text-red-700' },
            { label: 'With Alerts',   value: classrooms.filter(c => c.activeAlerts > 0).length, style: dm ? 'bg-amber-900/20 border-amber-800 text-amber-400' : 'bg-amber-50 border-amber-100 text-amber-700' },
          ].map(({ label, value, style }) => (
            <div key={label} className={`px-4 py-2 rounded-xl border shadow-sm text-center ${style}`}>
              <p className="text-xl font-bold">{value}</p>
              <p className="text-xs opacity-70">{label}</p>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48 text-slate-400 text-sm">Loading classes…</div>
      ) : error ? (
        <div className="flex items-center justify-center h-48 text-red-400 text-sm gap-2">
          <XCircle className="h-4 w-4" /> {error}
        </div>
      ) : classrooms.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-slate-400 text-sm gap-2">
          <BookOpen className="h-8 w-8 text-slate-200" />
          No classrooms assigned
        </div>
      ) : (
        <div className="space-y-5">
          {high.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-2">Needs Attention</h3>
              <div className="space-y-2.5">
                {high.map(c => <ClassroomCard key={c.id} classroom={c} onClick={() => setSelected(c)} dm={dm} />)}
              </div>
            </section>
          )}
          {medium.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-amber-500 uppercase tracking-wide mb-2">Monitor Closely</h3>
              <div className="space-y-2.5">
                {medium.map(c => <ClassroomCard key={c.id} classroom={c} onClick={() => setSelected(c)} dm={dm} />)}
              </div>
            </section>
          )}
          {low.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold text-emerald-500 uppercase tracking-wide mb-2">All Clear</h3>
              <div className="space-y-2.5">
                {low.map(c => <ClassroomCard key={c.id} classroom={c} onClick={() => setSelected(c)} dm={dm} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
